import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

interface HbsCopyMenuProps {
  /** Prefix for this menu's buttons: `<idPrefix>-trigger`, `<idPrefix>-username`,
   *  `<idPrefix>-password`. Copy menus repeat (one per safe row), so the parent
   *  derives this from its item. */
  idPrefix?: string;
  onCopyUsername: () => void;
  onCopyPassword: () => void;
  hasUsername?: boolean;
  hasPassword?: boolean;
  ariaLabel?: string;
}

export function HbsCopyMenu({
  idPrefix,
  onCopyUsername,
  onCopyPassword,
  hasUsername = true,
  hasPassword = true,
  ariaLabel = 'Copy',
}: HbsCopyMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  // The safe list nests clipping boxes: each family group is a
  // `overflow-hidden` <section>, all inside the `overflow-y-auto` list. A menu
  // positioned `absolute` inside the row gets clipped by whichever box it
  // spills out of — the last item in a family section couldn't reach Password.
  // Render it in a portal with fixed coordinates so it escapes every clipper.
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place(): void {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const itemCount = (hasUsername ? 1 : 0) + (hasPassword ? 1 : 0);
      const estMenuHeight = itemCount * 34 + 12; // ~34px/item + padding
      const right = window.innerWidth - rect.right; // right-align to the button
      const roomBelow = window.innerHeight - rect.bottom;
      setPos(roomBelow >= estMenuHeight + 8
        ? { top: rect.bottom + 4, right }
        : { bottom: window.innerHeight - rect.top + 4, right });
    }
    place();
    // Keep the menu glued to the button as the list scrolls or the window resizes.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, hasUsername, hasPassword]);

  useEffect(() => {
    if (!open) return;
    firstItemRef.current?.focus();

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    function onMouseDown(e: MouseEvent): void {
      const t = e.target as Node;
      // The menu lives in a portal, so it's outside containerRef — check both.
      if (!containerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  function handleUsername(): void {
    onCopyUsername();
    setOpen(false);
  }

  function handlePassword(): void {
    onCopyPassword();
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        id={idPrefix ? `${idPrefix}-trigger` : undefined}
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="p-1.5 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
      >
        <Icon name="copy" size={14} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: 50 }}
          className="min-w-[140px] bg-hbs-surface border border-hbs-border rounded-hbs-md shadow-hbs-md py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {hasUsername && (
            <button
              id={idPrefix ? `${idPrefix}-username` : undefined}
              ref={firstItemRef}
              type="button"
              role="menuitem"
              onClick={handleUsername}
              className="block w-full text-left px-3 py-1.5 text-[13px] text-hbs-text hover:bg-hbs-surface-alt"
            >
              Copy username
            </button>
          )}
          {hasPassword && (
            <button
              id={idPrefix ? `${idPrefix}-password` : undefined}
              type="button"
              role="menuitem"
              onClick={handlePassword}
              className="block w-full text-left px-3 py-1.5 text-[13px] text-hbs-text hover:bg-hbs-surface-alt"
            >
              Copy password
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
